import {Attribute} from '../../../components/ObjectAttribute';
import {Json} from '../../../components/JsonObjectCard';
import {RequestExample} from '../../../components/RequestExampleCard';
import {RequestDefinitionData} from '../../../components/RequestDefinition';

const addWorkOrderItemRequest: RequestExample = {
    title: {
        verb: 'POST',
        endpointUrl: '/api/work-orders/:id/items',
    },
    curl:
        `curl --location \\
--request POST '<YOUR SERVER>/api/work-orders/:id/items' \\
--header 'Content-Type: application/json' \\
--header 'Authorization: Bearer <TOKEN>' \\
--data-raw '{
    "part": {
        "name": "PM100"
    },
    "type": {
        "id": 20
    },
    "uom": {
        "abbreviation": "ea"
    },
    "description": "Entry Bike Pump",
    "quantity": "2",
    "cost": "12.50"
}'
`,
};

const addWorkOrderItemResponse: Json = {
    title: 'RESPONSE',
    json: {
        "id": 41,
        "number": "1024:1",
        "status": "Entered",
        "moItem": {
            "id": 385,
            "description": "Bike, 26 inch"
        },
        "locationGroup": {
            "id": 5,
            "name": "SLC"
        },
        "customer": {
            "id": 7,
            "name": "Sam Ash"
        },
        "class": {
            "id": 1,
            "name": "None"
        },
        "category": {
            "id": 12,
            "name": "Assembly"
        },
        "quantityOrdered": 1,
        "quantityTarget": 1,
        "quantityScrapped": 0,
        "cost": "0",
        "note": "",
        "dateCreated": "2021-05-14T15:49:18.000-0600",
        "dateScheduled": "2021-05-14T15:49:08.160-0600",
        "dateScheduledToStart": "2021-05-14T15:49:08.160-0600",
        "dateStarted": null,
        "dateFinished": null,
        "lastModified": {
            "username": "admin",
            "dateLastModified": "2021-05-14T15:49:18.744-0600"
        },
        "assignedUsers": [
            {
                "id": 1,
                "name": "admin",
                "description": "Administrator"
            }
        ],
        "woItems": [
            {
                "id": 101,
                "part": {
                    "id": 80,
                    "name": "BK100",
                    "description": "New to Biking Kit"
                },
                "type": {
                    "id": 10,
                    "name": "Finished Good"
                },
                "uom": {
                    "id": 1,
                    "name": "Each",
                    "abbreviation": "ea"
                },
                "quantity": "1",
                "quantityUsed": "0",
                "quantityScrapped": "0",
                "cost": "0",
                "description": "New to Biking Kit",
                "destLocation": {
                    "id": 23,
                    "name": "Finished Goods"
                },
                "consumeItems": []
            },
            {
                "id": 103,
                "part": {
                    "id": 52,
                    "name": "PM100",
                    "description": "Entry Bike Pump"
                },
                "type": {
                    "id": 20,
                    "name": "Raw Good"
                },
                "uom": {
                    "id": 1,
                    "name": "Each",
                    "abbreviation": "ea"
                },
                "quantity": "2",
                "quantityUsed": "0",
                "quantityScrapped": "0",
                "cost": "12.50",
                "description": "Entry Bike Pump",
                "destLocation": null,
                "consumeItems": []
            }
        ],
        "steps": null
    },
};

const addWorkOrderItemAttributes: Attribute[] = [
    {
        name: 'part',
        type: 'object',
        optional: true,
        description: 'The part to associate with the item, resolved by id when one is supplied and by part number ' +
            '(name) otherwise. Required for raw good and finished good items; omit for a note. ' +
            'Returns 404 when no matching part exists.',
        children: [
            {
                name: 'id',
                type: 'integer',
                optional: true,
                description: 'The id of the part. Takes precedence over the part number when both are supplied.',
            },
            {
                name: 'name',
                type: 'string',
                optional: true,
                description: 'The part number. Used when no id is supplied.',
            },
        ],
    },
    {
        name: 'type',
        type: 'object',
        optional: true,
        description: 'The work order item type, resolved by id when one is supplied and by name otherwise. ' +
            'Supported values: 20 (Raw Good), 10 (Finished Good), and 40 (Note). ' +
            'Defaults to Note when omitted; any other value is rejected.',
        children: [
            {
                name: 'id',
                type: 'integer',
                optional: true,
                description: 'The id of the item type. Takes precedence over the name when both are supplied.',
            },
            {
                name: 'name',
                type: 'string',
                optional: true,
                description: 'The name of the item type. Used when no id is supplied.',
            },
        ],
    },
    {
        name: 'description',
        type: 'string',
        description: 'The description of the item. Required.',
    },
    {
        name: 'quantity',
        type: 'quantity',
        description: 'The quantity to fulfill. Must be greater than zero.',
    },
    {
        name: 'uom',
        type: 'object',
        optional: true,
        description: 'The unit of measure of the item, resolved by id when one is supplied and by code (abbreviation) ' +
            'otherwise. Defaults to the part\'s unit of measure when omitted, or to Each when there is no part. ' +
            'Must be compatible with the part. Returns 404 when it does not match a unit of measure.',
        children: [
            {
                name: 'id',
                type: 'integer',
                optional: true,
                description: 'The id of the unit of measure. Takes precedence over the code when both are supplied.',
            },
            {
                name: 'abbreviation',
                type: 'string',
                optional: true,
                description: 'The unit of measure code. Used when no id is supplied.',
            },
        ],
    },
    {
        name: 'cost',
        type: 'money',
        optional: true,
        description: 'The unit cost of the item. Defaults to the part\'s current unit cost when a part is supplied, otherwise zero.',
    },
];

const data: RequestDefinitionData = {
    id: 'add-work-order-item',
    title: 'Add a work order item',
    description: 'Adds a raw good, finished good, or note item to an existing work order and returns the updated work order. ' +
        'The request body is a work order item — the same object the work order GET request returns for each item — ' +
        'so an item can be copied out of a work order and posted back without transformation. The read-only fields ' +
        'id, quantityUsed, quantityScrapped, destLocation, and consumeItems are ignored. ' +
        'Requires WO_EDIT access right and full (non-read-only) access to the work order\'s location group; ' +
        'read-only access returns a 403. Users without WO_VIEW_ALL_ASSIGNED access right can only add items to ' +
        'work orders they are assigned to; otherwise a 403 is returned. ' +
        'Returns 404 if the work order is not found and 409 if it is already fulfilled.',
    endpoint: {
        verb: 'POST',
        path: '/work-orders/:id/items',
        tooltip: 'Add a work order item',
    },
    attributes: addWorkOrderItemAttributes,
    requestObject: addWorkOrderItemRequest,
    responseObject: addWorkOrderItemResponse,
};

export default data;
